export const TECHNICAL_REPORT_CACHE_VERSION = 2
export const TECHNICAL_REPORT_CACHE_PREFIX = `specific:technical-reports:v${TECHNICAL_REPORT_CACHE_VERSION}:`
export const LEGACY_TECHNICAL_REPORT_STORAGE_KEY = 'specific-technical-reports-v1'

const normalizeIdentifier = (value, {lowercase = false} = {}) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return ''
  }

  const normalized = String(value).trim()
  return lowercase ? normalized.toLowerCase() : normalized
}

const getBrowserStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage || null
  } catch (error) {
    return null
  }
}

export const getTechnicalReportTenantDomain = () => {
  if (typeof window === 'undefined') {
    return ''
  }

  return normalizeIdentifier(window.location?.hostname, {lowercase: true})
}

export const buildTechnicalReportCacheKey = ({tenantDomain, currentUser} = {}) => {
  const tenant = normalizeIdentifier(tenantDomain, {lowercase: true})
  const companyId = normalizeIdentifier(currentUser?.company_id || currentUser?.company?.id)
  const explicitUserId = currentUser?.user_id || currentUser?.id
  const userId = explicitUserId
    ? normalizeIdentifier(explicitUserId)
    : normalizeIdentifier(currentUser?.email, {lowercase: true})

  if (!tenant || !companyId || !userId) {
    return null
  }

  return `${TECHNICAL_REPORT_CACHE_PREFIX}${encodeURIComponent(tenant)}:${encodeURIComponent(companyId)}:${encodeURIComponent(userId)}`
}

export const readTechnicalReportCache = (storageKey, storage = getBrowserStorage()) => {
  if (!storageKey || !storage) {
    return []
  }

  try {
    const parsed = JSON.parse(storage.getItem(storageKey) || '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

export const writeTechnicalReportCache = (storageKey, reports, storage = getBrowserStorage()) => {
  if (!storageKey || !storage || !Array.isArray(reports)) {
    return
  }

  try {
    storage.setItem(storageKey, JSON.stringify(reports))
  } catch (error) {
    // Storage can be unavailable or full. The in-memory report list remains authoritative.
  }
}

export const removeLegacyTechnicalReportCache = (storage = getBrowserStorage()) => {
  try {
    storage?.removeItem(LEGACY_TECHNICAL_REPORT_STORAGE_KEY)
  } catch (error) {
    // A blocked storage implementation must not interrupt report loading or logout.
  }
}

export const clearTechnicalReportCacheForUser = ({
  currentUser,
  tenantDomain = getTechnicalReportTenantDomain(),
} = {}, storage = getBrowserStorage()) => {
  if (!storage) {
    return
  }

  const storageKey = buildTechnicalReportCacheKey({tenantDomain, currentUser})
  const ownedKeys = [storageKey, LEGACY_TECHNICAL_REPORT_STORAGE_KEY]

  ownedKeys
    .filter(Boolean)
    .forEach((key) => {
      try {
        storage.removeItem(key)
      } catch (error) {
        // Continue removing the remaining owned keys when one removal is blocked.
      }
    })
}

export const createTechnicalReportCacheState = (storageKey, reports = []) => ({
  storageKey: storageKey || null,
  reports: Array.isArray(reports) ? reports : [],
})

export const getVisibleTechnicalReports = (state, storageKey) => (
  state?.storageKey === (storageKey || null) && Array.isArray(state?.reports)
    ? state.reports
    : []
)
